import { useEffect } from 'react';

const focusableSelector = '[data-tv-focusable]:not([disabled]):not([aria-hidden="true"])';

function visible(element: HTMLElement) {
  const style = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  return style.visibility !== 'hidden' && style.display !== 'none' && rect.width > 0 && rect.height > 0;
}

function candidateScore(current: DOMRect, candidate: DOMRect, direction: 'left' | 'right' | 'up' | 'down') {
  const currentX = current.left + current.width / 2;
  const currentY = current.top + current.height / 2;
  const candidateX = candidate.left + candidate.width / 2;
  const candidateY = candidate.top + candidate.height / 2;
  const dx = candidateX - currentX;
  const dy = candidateY - currentY;
  if (direction === 'left' && dx >= -4) return Number.POSITIVE_INFINITY;
  if (direction === 'right' && dx <= 4) return Number.POSITIVE_INFINITY;
  if (direction === 'up' && dy >= -4) return Number.POSITIVE_INFINITY;
  if (direction === 'down' && dy <= 4) return Number.POSITIVE_INFINITY;
  const primary = direction === 'left' || direction === 'right' ? Math.abs(dx) : Math.abs(dy);
  const secondary = direction === 'left' || direction === 'right' ? Math.abs(dy) : Math.abs(dx);
  const overlap = direction === 'left' || direction === 'right'
    ? Math.max(0, Math.min(current.bottom, candidate.bottom) - Math.max(current.top, candidate.top))
    : Math.max(0, Math.min(current.right, candidate.right) - Math.max(current.left, candidate.left));
  return primary + secondary * (overlap > 0 ? 0.24 : 0.65);
}

function moveFocus(direction: 'left' | 'right' | 'up' | 'down') {
  const current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const elements = [...document.querySelectorAll<HTMLElement>(focusableSelector)].filter(visible);
  if (!elements.length) return;
  if (!current || !current.matches(focusableSelector)) {
    elements[0]?.focus();
    return;
  }
  const rect = current.getBoundingClientRect();
  const ranked = elements
    .filter((element) => element !== current)
    .map((element) => ({ element, score: candidateScore(rect, element.getBoundingClientRect(), direction) }))
    .filter((entry) => Number.isFinite(entry.score))
    .sort((a, b) => a.score - b.score);
  ranked[0]?.element.focus({ preventScroll: true });
  ranked[0]?.element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
}

function normalizeKey(key: string): string {
  const map: Record<string, string> = {
    Left: 'ArrowLeft',
    Right: 'ArrowRight',
    Up: 'ArrowUp',
    Down: 'ArrowDown',
    Select: 'Enter',
    OK: 'Enter',
    GoBack: 'Escape',
    BrowserBack: 'Escape',
  };
  return map[key] || key;
}

export function useTvNavigation(onBack: () => void) {
  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      const key = normalizeKey(event.key);
      const target = event.target as HTMLElement | null;
      const isTextInput = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
      if (isTextInput && !['ArrowUp', 'ArrowDown', 'Escape'].includes(key)) return;
      if (key.startsWith('Arrow')) {
        event.preventDefault();
        moveFocus(key.slice(5).toLowerCase() as 'left' | 'right' | 'up' | 'down');
      } else if (key === 'Enter' && document.activeElement instanceof HTMLElement) {
        if (document.activeElement.matches(focusableSelector) && document.activeElement.tagName !== 'BUTTON' && document.activeElement.tagName !== 'A') {
          event.preventDefault();
          document.activeElement.click();
        }
      } else if (key === 'Escape' || key === 'Backspace') {
        if (isTextInput && target instanceof HTMLInputElement && target.value) return;
        event.preventDefault();
        onBack();
      }
    };
    const remote = (event: Event) => handle(new KeyboardEvent('keydown', { key: String((event as CustomEvent).detail) }));
    window.addEventListener('keydown', handle);
    window.addEventListener('pipi:remote-key', remote);
    return () => {
      window.removeEventListener('keydown', handle);
      window.removeEventListener('pipi:remote-key', remote);
    };
  }, [onBack]);
}

export function focusFirst(container?: HTMLElement | null) {
  const root = container ?? document;
  window.requestAnimationFrame(() => root.querySelector<HTMLElement>(focusableSelector)?.focus({ preventScroll: true }));
}
